import { ArrowPathRoundedSquareIcon } from '@heroicons/react/24/outline';
import { Button } from '@library/components/buttons';
import type { GamePlayerProjectionTrading } from '@library/types/trading';
import { NumberInput } from '@library/components/inputs';
import { Tooltip } from '@library/components/tooltip/Tooltip';
import { blockInvalidNumberCharIncludingNegative } from '@library/component-utils/input';
import { useBulkRankActions } from '@bo-hooks/simulations/useBulkRankActions';
import { useDebounceFn } from '@library/hooks/useDebounce';

export interface BulkRankActionsProps {
  newAndChangedProjections: Array<GamePlayerProjectionTrading>;
  setSimProjections: (
    simsProjections: Array<GamePlayerProjectionTrading>
  ) => void;
  simProjections: Array<GamePlayerProjectionTrading>;
  statTypeName: string;
  updateProjection: ({
    change,
    initialProjection
  }: {
    change: GamePlayerProjectionTrading;
    initialProjection: GamePlayerProjectionTrading;
  }) => void;
}

export const BulkRankActions = ({
  newAndChangedProjections,
  setSimProjections,
  simProjections,
  statTypeName,
  updateProjection
}: BulkRankActionsProps) => {
  const { handleBulkRankChange, handleReorderByRank } = useBulkRankActions({
    newAndChangedProjections,
    setSimProjections,
    simProjections,
    updateProjection
  });

  const { debounce: debouncedOnBulkRankChange } = useDebounceFn(
    handleBulkRankChange as (...args: unknown[]) => void,
    500
  );

  return (
    <div className="flex items-center gap-4">
      <Tooltip text="Putting a number in this input will assign all ranks in this stat type starting from the inputted number">
        <div className="flex items-center gap-4">
          <span className="font-heebo text-soClean-140 text-sm font-medium tracking-wide">
            Starting rank
          </span>
          <NumberInput
            className="bg-soFresh-110 text-soClean-100 border-soFresh-130 focus:border-msJackson h-10 w-12 rounded-md border p-0 text-center text-sm"
            id={`${statTypeName}-bulk-rank`}
            onBlur={(event) => {
              event.currentTarget.value = '';
            }}
            onKeyDown={blockInvalidNumberCharIncludingNegative}
            onValueChange={debouncedOnBulkRankChange}
            placeholder="-"
          />
        </div>
      </Tooltip>
      <Tooltip childClassName="h-6 w-4 items-center" text="Reorder by Rank">
        <Button onClick={handleReorderByRank}>
          <ArrowPathRoundedSquareIcon className="size-6 shrink-0" />
        </Button>
      </Tooltip>
    </div>
  );
};
